import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchRoutes, type RoutesResponse, type RouteWithSlug } from '../lib/api';

const PAGE_SIZE = 20;

export interface UseRoutesFilters {
  country?: string;
  terrain?: string;
}

export interface UseRoutesResult {
  routes: RouteWithSlug[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function useRoutes(filters?: UseRoutesFilters): UseRoutesResult {
  const {
    data,
    isLoading,
    isError,
    isSuccess,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<RoutesResponse, Error>({
    queryKey: ['routes', filters?.country, filters?.terrain],
    queryFn: async ({ pageParam }) => {
      return fetchRoutes({
        country: filters?.country,
        terrain: filters?.terrain,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) {
        return undefined;
      }
      // Calculate next offset based on total routes fetched
      const totalFetched = allPages.reduce((acc, page) => acc + page.routes.length, 0);
      return totalFetched;
    },
  });

  // Flatten all pages into a single routes array
  const routes = data?.pages.flatMap(page => page.routes) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    routes,
    total,
    isLoading,
    isError,
    isSuccess,
    error,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  };
}
